<template lang="pug">
.vue-time-entries-calendar
  AppTimeGrid(
    :default-date="date"
    :events="eventsForFullCalendar"
    :dates="dateRange"
    :time-zone="currentZone.name"
    :first-day="startWeekOn + 1"
    :min-date="retentionDate"
    @date-select="onDateSelect"
    @open-dialog="onOpenDialog"
  )
</template>
<script>
import { mapGetters } from 'vuex';
import { ColorUtilsMixin } from './../../mixins';
import { parseInternalDatetime } from '~/utils/time_utils';
import { MANUAL_TIME_REQUEST_TIME_ENTRY_COLOR } from '~/app_constants';

export default {
  name: 'TimeEntriesCalendar',
  mixins: [ColorUtilsMixin],
  props: {
    timeEntries: Array,
  },
  computed: {
    ...mapGetters([
      'holidays',
      'timeOffRequests',
      'newTimeEntry',
      'startWeekOn',
      'isFuture',
      'currentZone',
      'getTimeEntry',
      'getProject',
      'getOrganization',
      'getTask',
      'addTimeDisabled',
      'retentionDate',
      'isBeforeRetentionDate',
      'dateRange',
      'date',
      'reasonsForTimeEntry',
    ]),
    eventsForFullCalendar() {
      return this.timeEntryEvents.concat(this.holidayEvents).concat(this.timeOffEvents);
    },
    holidayEvents() {
      return this.holidays.map((holiday) => {
        return {
          title: holiday.name,
          allDay: true,
          isRequest: false,
          start: this.toDate(holiday.date),
          backgroundColor: '#B8F1D6',
        };
      });
    },
    timeOffEvents() {
      return this.timeOffRequests.map((request) => {
        const color = this.stringToColor(request.policy_name);
        const organization = this.getOrganization(request.organization_id);
        let start = parseInternalDatetime(request.starts_at);
        // stops_at is 17:00 on the last PTO day, not end of day; bump a day so FC's exclusive end covers it.
        let stop = request.all_day
          ? parseInternalDatetime(request.stops_at).add(1, 'day')
          : parseInternalDatetime(request.stops_at).add(1, 'millisecond');

        // Full Calendar seems to have a problem with an event starting and stopping at the exact same time...hence the need to add 1 millisecond if they are the same time.
        if (!request.all_day && start.isSame(stop)) {
          stop.add(1, 'second');
        }
        start = request.all_day ? this.formatAbsoluteDate(start) : this.toDate(start);
        stop = request.all_day ? this.formatAbsoluteDate(stop) : this.toDate(stop);

        return {
          title: request.policy_name,
          allDay: request.all_day,
          isRequest: true,
          start,
          end: stop,
          backgroundColor: request.all_day ? '#FFE5C6' : '#FFB457',
          avatarColor: color,
          request,
          organization,
        };
      });
    },
    timeEntryEvents() {
      return this.timeEntries.map((timeEntry) => {
        const organization = this.getOrganization(timeEntry.organization_id);
        const project = this.getProject(timeEntry.project_id);
        const backgroundColor = timeEntry.manual_time_request
          ? MANUAL_TIME_REQUEST_TIME_ENTRY_COLOR
          : this.stringToColor(project.text || '');
        const task = this.getTask(timeEntry.task_id);

        return {
          start: this.toDate(timeEntry.start_time),
          end: this.toDate(timeEntry.stop_time),
          title: project.text || '',
          timeEntry,
          reasons: this.reasonsForTimeEntry(timeEntry),
          backgroundColor,
          organization,
          project,
          task,
        };
      });
    },
  },
  methods: {
    onOpenDialog(payload) {
      const timeEntry = this.getTimeEntry(payload.timeEntryId);
      let dispatchPayload;

      if (payload.type === 'edit') {
        dispatchPayload = { dialog: 'timeEntriesFormDialog', type: 'edit' };
      } else if (payload.type === 'split') {
        dispatchPayload = { dialog: 'timeEntriesSplitDialog' };
      } else {
        dispatchPayload = { dialog: 'timeEntriesDeleteDialog' };
      }
      this.$store.dispatch('openDialog', Object.assign({ timeEntry }, dispatchPayload));
    },
    formatAbsoluteDate(date) {
      return date.format('YYYY-MM-DD');
    },
    toDate(date) {
      if (moment.isMoment(date)) {
        return date.toDate();
      }

      return date;
    },
    openTimeEntryDialog(start, stop) {
      const timeEntry = Object.assign({}, this.newTimeEntry, { start_time: start, stop_time: stop });

      this.$store.dispatch('openDialog', { dialog: 'timeEntriesFormDialog', type: 'provided', timeEntry });
    },
    onDateSelect(payload) {
      const start = parseInternalDatetime(payload.startStr);
      const stop = parseInternalDatetime(payload.endStr);
      const oneDay = moment.duration(1, 'day');
      const greaterThanOneDay = stop - start - oneDay > 0;

      if (
        !(
          this.addTimeDisabled ||
          this.isFuture(start, 'seconds') ||
          this.isFuture(stop, 'seconds') ||
          greaterThanOneDay ||
          this.isBeforeRetentionDate(start)
        )
      ) {
        this.openTimeEntryDialog(start, stop);
      }
    },
  },
};
</script>
